import type {DungeonLight, WorldPoint} from "@/dungeons/model/DungeonGeometry.ts";
import {DEFAULT_LIGHT_COLOR_STRENGTH} from "@/dungeons/lighting/LightPresets.ts";
import type {DirectionalLight} from "@/dungeons/lighting/DirectionalLight.ts";

export type EncounterLightOverride = Pick<DungeonLight, "enabled" | "rangeMeters" | "intensity" | "color" | "colorStrength" | "animation"> & {
    lightId: string;
    position?: WorldPoint | null;
};

export type EncounterLight = Omit<DungeonLight, "position"> & {
    levelId: string;
    position: WorldPoint;
    combatantId?: string | null;
};

export type EncounterLights = {
    overrides: EncounterLightOverride[];
    lights: EncounterLight[];
    levelLightingOverrides?: LevelLightingOverride[];
};

export type LevelLightingOverride = {levelId: string; light: DirectionalLight};

export function effectiveLevelLighting(standard: DirectionalLight, levelId: string, state: EncounterLights): DirectionalLight {
    return state.levelLightingOverrides?.find(item => item.levelId === levelId)?.light ?? standard;
}

/** A settings preview never owns the light's position, which may change while the popover is open. */
export type EncounterLightPreview =
    | {lightId: string; values: Omit<EncounterLightOverride, "lightId" | "position">}
    | {id: string; values: Partial<Omit<EncounterLight, "id" | "position">>};

export function withLightPreview(state: EncounterLights, preview: EncounterLightPreview | undefined): EncounterLights {
    if (!preview) return state;
    if ("lightId" in preview) {
        const current = state.overrides.find(item => item.lightId === preview.lightId);
        return {...state, overrides: [...state.overrides.filter(item => item.lightId !== preview.lightId),
            {...preview.values, lightId: preview.lightId, position: current?.position}]};
    }
    const current = state.lights.find(item => item.id === preview.id);
    return current ? {...state, lights: state.lights.map(item => item.id === preview.id
        ? {...item, ...preview.values} : item)} : state;
}

export function movedDungeonLightOverride(light: DungeonLight, override: EncounterLightOverride | undefined,
    position: WorldPoint): EncounterLightOverride {
    return {
        lightId: light.id,
        enabled: override?.enabled ?? light.enabled,
        rangeMeters: override?.rangeMeters ?? light.rangeMeters,
        intensity: override?.intensity ?? light.intensity,
        colorStrength: override?.colorStrength ?? light.colorStrength ?? DEFAULT_LIGHT_COLOR_STRENGTH,
        color: override?.color ?? light.color,
        animation: override ? override.animation ?? null : light.animation ?? null,
        position,
    };
}
